/**
 * Load Bus Detail Page - Items List for Bus Loading
 *
 * Shows all Wasserij_Item__c for a route, grouped by stop.
 * Sorted by Route_Sequence__c DESC (last stop first → first in bus).
 * Allows individual and bulk item loading toggles.
 * Sets Status__c to "In bus" when loaded.
 * Real-time polling for multi-user collaboration.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import { WbButton, WbChip, WbFlag, WbLabelStrip, WbState, WbTile } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import type { BusLoadingStop, BusLoadingItem, BusLoadingItemsResponse } from '../lib/salesforce';

// Polling interval for real-time updates (10 seconds for detail page)
const POLLING_INTERVAL_MS = 10000;

// Track recently toggled items to prevent server overwriting optimistic updates
const RECENT_TOGGLE_GRACE_PERIOD_MS = 5000;

export function LoadBusDetailPage() {
  const { routeId } = useParams<{ routeId: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [routeInfo, setRouteInfo] = useState<BusLoadingItemsResponse['route'] | null>(null);
  const [stops, setStops] = useState<BusLoadingStop[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loadedItems, setLoadedItems] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Track items that were recently toggled locally (to prevent server overwrites during polling)
  const recentlyToggledRef = useRef<Map<string, number>>(new Map());

  // Track pending API calls to prevent double-clicks
  const [pendingToggle, setPendingToggle] = useState<Set<string>>(new Set());

  // Fetch items for the route
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    if (!routeId) return;

    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else if (!isLoading) {
        // Only show loading on initial load
      }
      setError(null);

      const response = await salesforceClient.getBusLoadingItems(routeId);
      console.log('[LoadBusDetailPage] API response:', response);

      if (response.success && response.data) {
        const data = response.data;
        setRouteInfo(data.route);
        setTotalItems(data.totalItems);
        setLoadedItems(data.loadedItems);

        // Merge server data with local optimistic updates
        const now = Date.now();
        const mergedStops = data.stops.map((stop: BusLoadingStop) => ({
          ...stop,
          items: stop.items.map((item: BusLoadingItem) => {
            const toggledAt = recentlyToggledRef.current.get(item.itemId);
            if (toggledAt && (now - toggledAt) < RECENT_TOGGLE_GRACE_PERIOD_MS) {
              // Keep local state for recently toggled items
              const currentItem = stops
                .flatMap((s: BusLoadingStop) => s.items)
                .find((i: BusLoadingItem) => i.itemId === item.itemId);
              if (currentItem) {
                return currentItem;
              }
            }
            return item;
          }),
        }));

        setStops(mergedStops);

        // Clean up old entries
        recentlyToggledRef.current.forEach((timestamp, itemId) => {
          if ((now - timestamp) >= RECENT_TOGGLE_GRACE_PERIOD_MS) {
            recentlyToggledRef.current.delete(itemId);
          }
        });
      } else {
        console.error('[LoadBusDetailPage] API error response:', response);
        setError(response.message || 'Kon items niet ophalen');
      }
    } catch (err) {
      console.error('Error fetching bus loading items:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden van items');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [routeId, stops, isLoading]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchItems();
  }, [routeId]); // Only on routeId change, not fetchItems

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Toggle single item
  const handleToggleItem = useCallback(async (item: BusLoadingItem) => {
    if (pendingToggle.has(item.itemId)) return;

    const newLoadedState = !item.isLoaded;

    // Optimistic update
    setStops(prevStops => prevStops.map((stop: BusLoadingStop) => ({
      ...stop,
      items: stop.items.map((i: BusLoadingItem) =>
        i.itemId === item.itemId ? { ...i, isLoaded: newLoadedState } : i
      ),
      allItemsLoaded: stop.items.every((i: BusLoadingItem) =>
        i.itemId === item.itemId ? newLoadedState : i.isLoaded
      ),
    })));

    // Update loaded count optimistically
    setLoadedItems(prev => newLoadedState ? prev + 1 : prev - 1);

    // Track as recently toggled
    recentlyToggledRef.current.set(item.itemId, Date.now());

    // Mark as pending
    setPendingToggle(prev => new Set(prev).add(item.itemId));

    try {
      const response = await salesforceClient.toggleItemsLoaded({
        itemIds: [item.itemId],
        loaded: newLoadedState,
      });

      if (!response.success) {
        // Revert on error
        console.error('[LoadBusDetailPage] Toggle failed:', response);
        setStops(prevStops => prevStops.map((stop: BusLoadingStop) => ({
          ...stop,
          items: stop.items.map((i: BusLoadingItem) =>
            i.itemId === item.itemId ? { ...i, isLoaded: !newLoadedState } : i
          ),
        })));
        setLoadedItems(prev => newLoadedState ? prev - 1 : prev + 1);
        recentlyToggledRef.current.delete(item.itemId);
      }
    } catch (err) {
      console.error('[LoadBusDetailPage] Toggle error:', err);
      // Revert on error
      setStops(prevStops => prevStops.map((stop: BusLoadingStop) => ({
        ...stop,
        items: stop.items.map((i: BusLoadingItem) =>
          i.itemId === item.itemId ? { ...i, isLoaded: !newLoadedState } : i
        ),
      })));
      setLoadedItems(prev => newLoadedState ? prev - 1 : prev + 1);
      recentlyToggledRef.current.delete(item.itemId);
    } finally {
      setPendingToggle(prev => {
        const next = new Set(prev);
        next.delete(item.itemId);
        return next;
      });
    }
  }, [pendingToggle]);

  // Toggle all items for a stop
  const handleToggleAllForStop = useCallback(async (stop: BusLoadingStop) => {
    // Check if any items in this stop are already pending
    const hasPendingItems = stop.items.some((item: BusLoadingItem) => pendingToggle.has(item.itemId));
    if (hasPendingItems) return;

    // Determine new state: if all are loaded, unload all; otherwise load all
    const newLoadedState = !stop.allItemsLoaded;
    const itemIds = stop.items.map((i: BusLoadingItem) => i.itemId);
    const itemsToChange = stop.items.filter((i: BusLoadingItem) => i.isLoaded !== newLoadedState);

    if (itemsToChange.length === 0) return;

    // Optimistic update
    setStops(prevStops => prevStops.map((s: BusLoadingStop) => {
      if (s.ritId !== stop.ritId) return s;
      return {
        ...s,
        items: s.items.map((i: BusLoadingItem) => ({ ...i, isLoaded: newLoadedState })),
        allItemsLoaded: newLoadedState,
      };
    }));

    // Update loaded count
    const countChange = newLoadedState
      ? itemsToChange.length
      : -itemsToChange.length;
    setLoadedItems(prev => prev + countChange);

    // Track as recently toggled
    itemIds.forEach((id: string) => recentlyToggledRef.current.set(id, Date.now()));

    // Mark all as pending
    setPendingToggle(prev => {
      const next = new Set(prev);
      itemIds.forEach((id: string) => next.add(id));
      return next;
    });

    try {
      const response = await salesforceClient.toggleItemsLoaded({
        itemIds: itemsToChange.map((i: BusLoadingItem) => i.itemId),
        loaded: newLoadedState,
      });

      if (!response.success) {
        // Revert on error
        console.error('[LoadBusDetailPage] Bulk toggle failed:', response);
        setStops(prevStops => prevStops.map((s: BusLoadingStop) => {
          if (s.ritId !== stop.ritId) return s;
          return {
            ...s,
            items: s.items.map((i: BusLoadingItem) => {
              const wasChanged = itemsToChange.some((c: BusLoadingItem) => c.itemId === i.itemId);
              return wasChanged ? { ...i, isLoaded: !newLoadedState } : i;
            }),
          };
        }));
        setLoadedItems(prev => prev - countChange);
        itemIds.forEach((id: string) => recentlyToggledRef.current.delete(id));
      }
    } catch (err) {
      console.error('[LoadBusDetailPage] Bulk toggle error:', err);
      // Revert on error
      setStops(prevStops => prevStops.map((s: BusLoadingStop) => {
        if (s.ritId !== stop.ritId) return s;
        return {
          ...s,
          items: s.items.map((i: BusLoadingItem) => {
            const wasChanged = itemsToChange.some((c: BusLoadingItem) => c.itemId === i.itemId);
            return wasChanged ? { ...i, isLoaded: !newLoadedState } : i;
          }),
        };
      }));
      setLoadedItems(prev => prev - countChange);
      itemIds.forEach((id: string) => recentlyToggledRef.current.delete(id));
    } finally {
      setPendingToggle(prev => {
        const next = new Set(prev);
        itemIds.forEach((id: string) => next.delete(id));
        return next;
      });
    }
  }, [pendingToggle]);

  // Calculate progress percentage
  const progressPct = totalItems > 0 ? Math.round((loadedItems / totalItems) * 100) : 100;
  const isComplete = loadedItems === totalItems && totalItems > 0;

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
  };

  useHeader(routeInfo?.routeName ?? 'Bus laden', totalItems > 0 ? `${loadedItems}/${totalItems}` : null);

  return (
    <div className="space-y-4 pb-[140px]">
      <Link to="/bus-loading" className="wb-focus inline-flex items-center gap-1 min-h-tap font-wb-heading font-bold text-xl text-wb-inkt">
        <span aria-hidden>‹</span> {routeInfo?.routeName || 'Route laden…'}
      </Link>
      {routeInfo?.date && (
        <p className="font-mono text-sm text-wb-inkt2 -mt-3 capitalize">{formatDate(routeInfo.date)}{routeInfo.driverName ? ` · ${routeInfo.driverName}` : ''}</p>
      )}
      <p className="text-sm text-wb-inkt2">Omgekeerde volgorde: laatste stop eerst, zodat de eerste stop bovenop ligt.</p>
      {!isLoading && !error && isComplete && <WbFlag kind="info" title="Alles ingeladen">{loadedItems} van {totalItems} items ({progressPct}%).</WbFlag>}

      {isRefreshing && <p className="font-mono text-sm text-wb-inkt2 text-center">Verversen…</p>}
      {isLoading && <WbState variant="laden" titel="Items laden…" />}
      {!isLoading && error && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
      {!isLoading && !error && stops.length === 0 && <WbState variant="leeg" titel="Geen items gevonden." tekst="Er zijn geen items voor deze route." />}

      {/* Per stop een label-strook, per kleed een tegel; afgevinkt = groen (klaar) */}
      {!isLoading && !error && stops.map((stop) => {
        const bezig = stop.items.some((i: BusLoadingItem) => pendingToggle.has(i.itemId));
        const alles = stop.allItemsLoaded;
        return (
          <section key={stop.ritId} className="rounded-wb-lg border-wb border-wb-rand bg-wb-vlak overflow-hidden">
            <WbLabelStrip nummer={stop.routeSequence} naam={stop.customerName} sub={stop.address ? stop.address.replace(/\s+/g, ' ') : undefined} type={stop.type} />
            <div className="p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {stop.items.map((item: BusLoadingItem) => (
                  <ItemTegel key={item.itemId} item={item} isPending={pendingToggle.has(item.itemId)} onToggle={() => handleToggleItem(item)} />
                ))}
              </div>
              <WbButton variant={alles ? 'secondary' : 'primary'} onClick={() => handleToggleAllForStop(stop)} disabled={bezig}>
                {alles ? 'Ingeladen' : 'Alles ingeladen'}
              </WbButton>
            </div>
          </section>
        );
      })}
    </div>
  );
}

// =============================================================================
// ITEM-TEGEL
// =============================================================================

interface ItemTegelProps {
  item: BusLoadingItem;
  isPending: boolean;
  onToggle: () => void;
}

function ItemTegel({ item, isPending, onToggle }: ItemTegelProps) {
  const maat = item.needsSurfaceCalculation === false
    ? `${item.quantity ?? 1}×`
    : [item.dimensions, item.m2 ? `${item.m2.toFixed(2)} m²` : null].filter(Boolean).join(' · ');
  return (
    <WbTile variant="klaar" selected={item.isLoaded} onToggle={onToggle} disabled={isPending} className={clsx(isPending && 'opacity-60')}>
      <span className="flex items-center gap-2 flex-wrap">
        <span className="font-mono">{item.itemName}</span>
        {item.packagingType && <WbChip kind={item.packagingType === 'opslag' ? 'wacht' : 'neutraal'}>{item.packagingType === 'opslag' ? 'Opslag' : 'Plastic'}</WbChip>}
        {item.photoUrl && (
          <a href={item.photoUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="wb-focus inline-flex min-h-[32px] items-center text-sm underline underline-offset-4 text-wb-inkt2">Foto</a>
        )}
      </span>
      <span className="block text-sm font-normal text-wb-inkt2">{[item.productName, maat].filter(Boolean).join(' · ')}</span>
    </WbTile>
  );
}
